import * as React from 'react';
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from 'react-query';
import apiClient from '../../request/http-common';
import {
    Container,
    Typography,
    Paper,
    Box,
    Grid,
    Card,
    CardContent,
    Button,
    Chip,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    TextField,
    Divider,
    Alert,
    CircularProgress,
    useTheme,
    useMediaQuery,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Collapse,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    List,
    ListItem,
    ListItemText,
    ListItemIcon
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import PublicIcon from '@mui/icons-material/Public';
import CakeIcon from '@mui/icons-material/Cake';
import SaveIcon from '@mui/icons-material/Save';
import AttachmentIcon from '@mui/icons-material/Attachment';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import BusinessIcon from '@mui/icons-material/Business';
import FolderIcon from '@mui/icons-material/Folder';
import HomeIcon from '@mui/icons-material/Home';
import BadgeIcon from '@mui/icons-material/Badge';
import ReceiptIcon from '@mui/icons-material/Receipt';
import SecurityIcon from '@mui/icons-material/Security';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import NotificationsIcon from '@mui/icons-material/Notifications';

const ViewInvestorSubscription = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    
    const [status, setStatus] = useState('');
    const [notes, setNotes] = useState('');
    const [expandedPanel, setExpandedPanel] = useState('investor-profile');

    const { data: subscription, isLoading, error, refetch } = useQuery(
        ['investor-subscription', id],
        () => fetchSubscription(),
        { enabled: !!id }
    );

    const updateStatusMutation = useMutation(
        (statusData) => apiClient.patch(`/api/investor/admin/subscriptions/${id}/status`, statusData),
        {
            onSuccess: () => {
                refetch();
                alert('Status updated successfully!');
            },
            onError: (error) => {
                console.error('Error updating status:', error);
                alert('Failed to update status');
            }
        }
    );

    const fetchSubscription = async () => {
        const response = await apiClient.get(`/api/investor/admin/subscriptions/${id}`);
        return response.data.data;
    };

    useEffect(() => {
        if (subscription) {
            setStatus(subscription.status || '');
            setNotes(subscription.notes || '');
        }
    }, [subscription]);

    const handleSaveStatus = () => {
        updateStatusMutation.mutate({ status, notes });
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'in_progress': return 'warning';
            case 'submitted': return 'primary';
            case 'approved': return 'success';
            case 'rejected': return 'error';
            default: return 'default';
        }
    };

    const formatStatus = (status) => {
        switch (status) {
            case 'in_progress': return 'In Progress';
            case 'submitted': return 'Submitted';
            default: return status?.charAt(0).toUpperCase() + status?.slice(1) || 'Unknown';
        }
    };

    const handleAccordionChange = (panel) => (event, isExpanded) => {
        setExpandedPanel(isExpanded ? panel : false);
    };

    // Helper function to format field names
    const formatFieldName = (field) => {
        return field.replace(/([A-Z])/g, ' $1')
                   .replace(/^./, str => str.toUpperCase())
                   .replace(/\s+/g, ' ')
                   .trim();
    };

    // Helper function to check if field contains file data
    const isFileField = (value) => {
        return typeof value === 'string' && value.startsWith('uploads/');
    };

    // Helper function to get file type
    const getFileType = (filename) => {
        const ext = filename.toLowerCase().split('.').pop();
        if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return 'image';
        if (['pdf'].includes(ext)) return 'pdf';
        return 'document';
    };

    // Helper function to render file preview
    const renderFilePreview = (filename, fieldName) => {
        const fileType = getFileType(filename);
        // Get base URL from apiClient and remove '/api' for file serving
        const baseUrl = apiClient.defaults.baseURL.replace('/api', '');
        const fileUrl = `${baseUrl}/${filename}`;

        return (
            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    {fileType === 'image' && <ImageIcon sx={{ mr: 1, color: '#0d9153' }} />}
                    {fileType === 'pdf' && <PictureAsPdfIcon sx={{ mr: 1, color: '#0d9153' }} />}
                    {fileType === 'document' && <AttachmentIcon sx={{ mr: 1, color: '#0d9153' }} />}
                    <Typography variant="subtitle2">{formatFieldName(fieldName)}</Typography>
                </Box>
                
                {fileType === 'image' && (
                    <Box sx={{ mb: 2 }}>
                        <img 
                            src={fileUrl} 
                            alt={fieldName}
                            style={{ 
                                maxWidth: '200px', 
                                maxHeight: '200px',
                                borderRadius: '4px',
                                border: '1px solid #ddd'
                            }}
                            onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'block';
                            }}
                        />
                        <Typography 
                            variant="body2" 
                            color="error" 
                            sx={{ display: 'none' }}
                        >
                            Image could not be loaded
                        </Typography>
                    </Box>
                )}
                
                <Button 
                    variant="outlined" 
                    size="small"
                    href={fileUrl}
                    target="_blank"
                    sx={{ backgroundColor: '#0d9153', color: 'white' }}
                >
                    View {fileType === 'image' ? 'Image' : 'File'}
                </Button>
            </Box>
        );
    };

    // Parse section data safely
    const getSectionData = () => {
        if (!subscription?.section_data) return {};
        try {
            if (typeof subscription.section_data === 'string') {
                return JSON.parse(subscription.section_data);
            }
            return subscription.section_data;
        } catch (e) {
            console.error('Error parsing section_data:', e);
            return {};
        }
    };

    const renderFormSection = (title, icon, fields, sectionKey) => {
        const sectionData = getSectionData();
        const sectionFields = fields.filter(field => sectionData[field] !== undefined && sectionData[field] !== null && sectionData[field] !== '');
        
        if (sectionFields.length === 0) return null;

        return (
            <Accordion 
                expanded={expandedPanel === sectionKey} 
                onChange={handleAccordionChange(sectionKey)}
                sx={{ mb: 2 }}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {icon}
                        <Typography variant="h6" sx={{ ml: 1 }}>
                            {title} ({sectionFields.length} items)
                        </Typography>
                    </Box>
                </AccordionSummary>
                <AccordionDetails>
                    <Grid container spacing={2}>
                        {sectionFields.map((field) => {
                            const value = sectionData[field];
                            return (
                                <Grid item xs={12} sm={6} md={4} key={field}>
                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                            {formatFieldName(field)}
                                        </Typography>
                                        <Typography variant="body1">
                                            {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                                        </Typography>
                                    </Box>
                                </Grid>
                            );
                        })}
                    </Grid>
                </AccordionDetails>
            </Accordion>
        );
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (error || !subscription) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                <Alert severity="error">
                    Subscription not found or error loading data.
                </Alert>
                <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/admin/investor-subscriptions')}
                    sx={{ mt: 2 }}
                >
                    Back to Subscriptions
                </Button>
            </Container>
        );
    }

    const sectionData = getSectionData();

    return (
        <Container maxWidth="lg" sx={{ mt: isMobile ? 2 : 4, mb: 4, px: isMobile ? 1 : 3 }}>
            {/* Header */}
            <Box sx={{ mb: 3 }}>
                <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/admin/investor-subscriptions')}
                    sx={{ mb: 2 }}
                >
                    Back to Subscriptions
                </Button>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box>
                        <Typography variant={isMobile ? "h5" : "h4"} gutterBottom>
                            {subscription.organization_name || subscription.fund_name || 'Individual Subscription'}
                        </Typography>
                        <Typography variant="subtitle1" color="text.secondary">
                            Subscription ID: {subscription.id} | Progress: {subscription.overall_progress || 0}%
                        </Typography>
                    </Box>
                    <Chip 
                        label={formatStatus(subscription.status)} 
                        color={getStatusColor(subscription.status)} 
                        size={isMobile ? "small" : "medium"}
                    />
                </Box>
            </Box>

            <Grid container spacing={3}>
                {/* Main Content */}
                <Grid item xs={12} md={8}>
                    {/* Basic Information */}
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Overview
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                                        <Box>
                                            <Typography variant="body2" color="text.secondary">Applicant</Typography>
                                            <Typography variant="body1">{sectionData.applicant_name || subscription.user?.name || 'Not provided'}</Typography>
                                        </Box>
                                    </Box>
                                </Grid>
                                
                                <Grid item xs={12} sm={6}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
                                        <Box>
                                            <Typography variant="body2" color="text.secondary">Email</Typography>
                                            <Typography variant="body1">{sectionData.primaryEmail || subscription.user?.email || 'Not provided'}</Typography>
                                        </Box>
                                    </Box>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />
                                        <Box>
                                            <Typography variant="body2" color="text.secondary">Phone</Typography>
                                            <Typography variant="body1">{sectionData.primaryPhone || 'Not provided'}</Typography>
                                        </Box>
                                    </Box>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <BusinessIcon sx={{ mr: 1, color: 'text.secondary' }} />
                                        <Box>
                                            <Typography variant="body2" color="text.secondary">Fund Name</Typography>
                                            <Typography variant="body1">{subscription.fund_name || 'Not specified'}</Typography>
                                        </Box>
                                    </Box>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <FolderIcon sx={{ mr: 1, color: 'text.secondary' }} />
                                        <Box>
                                            <Typography variant="body2" color="text.secondary">Investment Type</Typography>
                                            <Typography variant="body1">{sectionData.investment_type || subscription.entity_type || 'Not specified'}</Typography>
                                        </Box>
                                    </Box>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <CakeIcon sx={{ mr: 1, color: 'text.secondary' }} />
                                        <Box>
                                            <Typography variant="body2" color="text.secondary">Submitted</Typography>
                                            <Typography variant="body1">
                                                {new Date(subscription.created_at).toLocaleDateString()}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* Detailed Form Sections */}
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                                Detailed Application Information
                            </Typography>

                            {/* Investor Profile */}
                            {renderFormSection(
                                'Personal Information',
                                <PersonIcon sx={{ color: '#0d9153' }} />,
                                ['investorName', 'firstName', 'middleName', 'lastName', 'dateOfBirth', 'socialSecurityNumber', 'legalResidentState', 'overAge18'],
                                'investor-profile'
                            )}

                            {/* Contact & Address */}
                            {renderFormSection(
                                'Contact & Address Information',
                                <HomeIcon sx={{ color: '#0d9153' }} />,
                                ['primaryPhone', 'primaryEmail', 'residenceAddress', 'city', 'state', 'zipCode', 'country', 'mailingAddressSame', 'mailingAddress', 'mailingCity', 'mailingState', 'mailingZipCode'],
                                'contact-info'
                            )}

                            {/* Banking Information */}
                            {renderFormSection(
                                'Banking & Wire Instructions',
                                <AccountBalanceIcon sx={{ color: '#0d9153' }} />,
                                ['bankName', 'bankAddress', 'routingNumber', 'accountName', 'accountNumber', 'wiringBankName', 'isWiringBankFATF', 'isInvestorCustomerOfBank'],
                                'banking-info'
                            )}

                            {/* Tax Information */}
                            {renderFormSection(
                                'Tax Information',
                                <ReceiptIcon sx={{ color: '#0d9153' }} />,
                                ['taxExemptFromUSFederalIncomeTax', 'isGrantorTrustOrDisregardedEntity', 'selectedTaxDocument', 'taxResidency', 'foreignTaxIdNumber', 'treatyBenefits', 'exemptFromUSTax', 'w9EntityName', 'w8EntityName', 'w8CountryOfIncorporation'],
                                'tax-info'
                            )}

                            {/* AML Information */}
                            {renderFormSection(
                                'AML & Compliance',
                                <SecurityIcon sx={{ color: '#0d9153' }} />,
                                ['sourceOfWealth', 'purposeOfAccount', 'isPoliticallyExposed', 'politicalExposureDetails', 'hasConvictions', 'convictionDetails', 'isSanctioned', 'sanctionDetails', 'howDidYouHear'],
                                'aml-info'
                            )}

                            {/* Accredited Investor Information */}
                            {renderFormSection(
                                'Accredited Investor Verification',
                                <VerifiedUserIcon sx={{ color: '#0d9153' }} />,
                                ['accreditedInvestorStatus', 'accreditedInvestorType', 'individualNetWorthOver1Million', 'netWorthCertification', 'incomeCertification', 'investmentAmount', 'minimumInvestment', 'maximumInvestment'],
                                'accredited-info'
                            )}

                            {/* Electronic Communications */}
                            {renderFormSection(
                                'Communication Preferences',
                                <NotificationsIcon sx={{ color: '#0d9153' }} />,
                                ['consentToElectronicDelivery', 'electronicConsent', 'electronicDelivery', 'emailUpdates', 'smsNotifications'],
                                'communication-prefs'
                            )}

                            {/* Investment Details */}
                            {renderFormSection(
                                'Investment & Purpose',
                                <BusinessIcon sx={{ color: '#0d9153' }} />,
                                ['isTransferRelated', 'investmentPurpose', 'hasInvestmentAdvisor', 'investmentAdvisorName', 'investmentAdvisorFirm', 'specialInstructions', 'additionalComments'],
                                'investment-details'
                            )}
                        </CardContent>
                    </Card>

                    {/* Uploaded Files */}
                    {sectionData.uploaded_files && (
                        <Card sx={{ mb: 3 }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Uploaded Documents
                                </Typography>
                                {/* Debug info removed - files are now properly displayed below */}
                                {(() => {
                                    const hasFiles = Object.values(sectionData.uploaded_files).some(files => 
                                        files && Array.isArray(files) && files.length > 0
                                    );
                                    
                                    if (!hasFiles) {
                                        return (
                                            <Box sx={{ textAlign: 'center', py: 4 }}>
                                                <FolderIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                                                <Typography variant="body1" color="text.secondary">
                                                    No documents have been uploaded yet.
                                                </Typography>
                                            </Box>
                                        );
                                    }
                                    
                                    return (
                                        <Grid container spacing={2}>
                                            {Object.entries(sectionData.uploaded_files).map(([fieldName, files]) => {
                                                if (!files || !Array.isArray(files) || files.length === 0) return null;
                                        return (
                                            <Grid item xs={12} sm={6} md={4} key={fieldName}>
                                                <Box sx={{ mb: 2 }}>
                                                    <Typography variant="subtitle2" gutterBottom>
                                                        {formatFieldName(fieldName)}
                                                    </Typography>
                                                    {files.map((file, index) => {
                                                        const fileName = file.name || 'Unknown file';
                                                        const fileSize = file.size ? Math.round(file.size / 1024) : 0;
                                                        const fileType = getFileType(fileName);
                                                        
                                                        // Use the URL provided by the backend (Laravel asset helper)
                                                        let fileUrl = null;
                                                        if (file.uploaded && file.url) {
                                                            // Use the full URL provided by Laravel's asset() helper
                                                            fileUrl = file.url;
                                                            console.log('📁 Using backend URL:', fileUrl);
                                                        } else if (file.uploaded && fileName) {
                                                            // Fallback: construct URL if backend URL is missing
                                                            const baseUrl = apiClient.defaults.baseURL.replace('/api', '');
                                                            fileUrl = `${baseUrl}/uploads/documents/${fieldName}/${fileName}`;
                                                            console.log('📁 Fallback URL constructed:', fileUrl);
                                                        } else {
                                                            console.log('⚠️ File not uploaded or missing data:', file);
                                                        }
                                                        
                                                        return (
                                                            <Box key={index} sx={{ mb: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                                    {fileType === 'image' && <ImageIcon sx={{ mr: 1, color: '#0d9153' }} />}
                                                                    {fileType === 'pdf' && <PictureAsPdfIcon sx={{ mr: 1, color: '#0d9153' }} />}
                                                                    {fileType === 'document' && <AttachmentIcon sx={{ mr: 1, color: '#0d9153' }} />}
                                                                    <Typography variant="body2" fontWeight="medium">
                                                                        {fileName}
                                                                    </Typography>
                                                                </Box>
                                                                
                                                                <Typography 
                                                                    variant="caption" 
                                                                    color={file.uploaded ? "text.secondary" : "warning.main"} 
                                                                    display="block" 
                                                                    sx={{ 
                                                                        mb: 1,
                                                                        fontWeight: file.uploaded ? 'normal' : 'medium'
                                                                    }}
                                                                >
                                                                    Size: {fileSize}KB | Status: {file.uploaded ? '✅ Uploaded' : '⏳ Pending Upload'}
                                                                </Typography>
                                                                
                                                                {fileUrl && file.uploaded ? (
                                                                    <Box sx={{ mt: 1 }}>
                                                                        {fileType === 'image' && (
                                                                            <Box sx={{ mb: 2 }}>
                                                                                <img 
                                                                                    src={fileUrl} 
                                                                                    alt={fileName}
                                                                                    style={{ 
                                                                                        maxWidth: '150px', 
                                                                                        maxHeight: '150px',
                                                                                        borderRadius: '4px',
                                                                                        border: '1px solid #ddd',
                                                                                        cursor: 'pointer'
                                                                                    }}
                                                                                    onClick={() => window.open(fileUrl, '_blank')}
                                                                                    onError={(e) => {
                                                                                        e.target.style.display = 'none';
                                                                                        e.target.nextSibling.style.display = 'block';
                                                                                    }}
                                                                                />
                                                                                <Typography 
                                                                                    variant="caption" 
                                                                                    color="error" 
                                                                                    sx={{ display: 'none' }}
                                                                                >
                                                                                    Image preview unavailable
                                                                                </Typography>
                                                                            </Box>
                                                                        )}
                                                                        
                                                                        <Button 
                                                                            variant="outlined" 
                                                                            size="small"
                                                                            startIcon={fileType === 'image' ? <ImageIcon /> : fileType === 'pdf' ? <PictureAsPdfIcon /> : <AttachmentIcon />}
                                                                            href={fileUrl}
                                                                            target="_blank"
                                                                            sx={{ 
                                                                                backgroundColor: '#0d9153', 
                                                                                color: 'white',
                                                                                '&:hover': {
                                                                                    backgroundColor: '#0a7a42'
                                                                                }
                                                                            }}
                                                                        >
                                                                            View {fileType === 'image' ? 'Image' : fileType === 'pdf' ? 'PDF' : 'Document'}
                                                                        </Button>
                                                                    </Box>
                                                                ) : (
                                                                    <Box>
                                                                        <Typography variant="caption" color="error" display="block" sx={{ mb: 0.5 }}>
                                                                            File not available for viewing
                                                                        </Typography>
                                                                        {!file.uploaded && (
                                                                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                                                                This file was selected but not uploaded to the server.
                                                                            </Typography>
                                                                        )}
                                                                    </Box>
                                                                )}
                                                            </Box>
                                                        );
                                                    })}
                                                </Box>
                                            </Grid>
                                        );
                                    })}
                                        </Grid>
                                    );
                                })()}
                            </CardContent>
                        </Card>
                    )}
                </Grid>

                {/* Status Management Sidebar */}
                <Grid item xs={12} md={4}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Manage Status
                            </Typography>
                            
                            <FormControl fullWidth sx={{ mb: 3 }}>
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={status}
                                    onChange={(e) => setStatus(e.target.value)}
                                    label="Status"
                                >
                                    <MenuItem value="in_progress">In Progress</MenuItem>
                                    <MenuItem value="submitted">Submitted</MenuItem>
                                    <MenuItem value="approved">Approved</MenuItem>
                                    <MenuItem value="rejected">Rejected</MenuItem>
                                </Select>
                            </FormControl>

                            <TextField
                                fullWidth
                                label="Notes"
                                multiline
                                rows={4}
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                sx={{ mb: 3 }}
                            />

                            <Button
                                variant="contained"
                                fullWidth
                                startIcon={<SaveIcon />}
                                onClick={handleSaveStatus}
                                disabled={updateStatusMutation.isLoading}
                                sx={{ backgroundColor: '#0d9153' }}
                            >
                                {updateStatusMutation.isLoading ? 'Saving...' : 'Save Changes'}
                            </Button>
                        </CardContent>
                    </Card>

                    {/* Progress Information */}
                    <Card sx={{ mt: 2 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Progress Details
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Overall Progress: {subscription.overall_progress || 0}%
                            </Typography>
                            {subscription.submitted_at && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    Submitted: {new Date(subscription.submitted_at).toLocaleString()}
                                </Typography>
                            )}
                            {subscription.last_updated && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    Last Updated: {new Date(subscription.last_updated).toLocaleString()}
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Container>
    );
};

export default ViewInvestorSubscription; 